// Generated by `npm run data:reef-algae` (scripts/data/reefAlgae.ts) from the NCRMP
// extracts in data/ncrmp-benthic-strs/. Do not edit by hand: change the script
// and run it. tests/reefAlgae.test.ts builds it again and fails if this file
// is not what the archive gives.
import type { DepthBin, ReefIsland } from '../../reef';

/** NOAA's codes for the macroalgae and the crustose coralline algae (the file's TIER_3), each its kind and its name as the file gives it. */
export const ALGAE_CODES = [
  {
    code: 'EMA',
    kind: 'macroalgae',
    name: 'Encrusting macroalga'
  },
  {
    code: 'PESP',
    kind: 'macroalgae',
    name: 'Peyssonnelia sp'
  },
  {
    code: 'BGMA',
    kind: 'macroalgae',
    name: 'Blue-green macroalga'
  },
  {
    code: 'UPMA',
    kind: 'macroalgae',
    name: 'Upright macroalga'
  },
  {
    code: 'HALI',
    kind: 'macroalgae',
    name: 'Halimeda sp'
  },
  {
    code: 'PADI',
    kind: 'macroalgae',
    name: 'Padina sp'
  },
  {
    code: 'BRMA',
    kind: 'macroalgae',
    name: 'Brown macroalgae'
  },
  {
    code: 'RDMA',
    kind: 'macroalgae',
    name: 'Red macroalgae'
  },
  {
    code: 'ASPP',
    kind: 'macroalgae',
    name: 'Asparagopsis sp'
  },
  {
    code: 'MICR',
    kind: 'macroalgae',
    name: 'Microdictyon sp'
  },
  {
    code: 'GRMA',
    kind: 'macroalgae',
    name: 'Green macroalgae'
  },
  {
    code: 'DICO',
    kind: 'macroalgae',
    name: 'Dictyopteris/Dictyota'
  },
  {
    code: 'LOBO',
    kind: 'macroalgae',
    name: 'Lobophora sp'
  },
  {
    code: 'CAUL',
    kind: 'macroalgae',
    name: 'Caulerpa sp'
  },
  {
    code: 'AVSP',
    kind: 'macroalgae',
    name: 'Avrainvillea sp'
  },
  {
    code: 'NEOM',
    kind: 'macroalgae',
    name: 'Neomeris sp'
  },
  {
    code: 'CCAH',
    kind: 'coralline',
    name: 'CCA growing on hard substrate'
  },
  {
    code: 'CCAR',
    kind: 'coralline',
    name: 'CCA growing on rubble'
  }
] as const;
export type AlgaeCode = (typeof ALGAE_CODES)[number]['code'];

/** A stratum's algae: of the points on the bottom at its site visits, each code's mean share, percent. */
export interface StratumAlgae {
  island: ReefIsland;
  bin: DepthBin;
  sites: number;
  percent: Readonly<Record<AlgaeCode, number>>;
}

/**
 * The reef's limu and coralline by NOAA's names for them, stratum by stratum (M224, M225), in `stratumIndex` order:
 * NOAA's surveys' site visits in the square, 2015 to 2024, each its points' shares by code, their mean (Ecosystem
 * Sciences Division, Pacific Islands Fisheries Science Center (2026), NCEI Accession 0317464, CC0 1.0). MEASURED by the
 * script. For the Maui Nui Reef habitat's limu and coralline, drawn from the user's art since M243 to M245; never simulated.
 */
export const REEF_ALGAE: readonly StratumAlgae[] = [
  { island: 'maui', bin: 'shallow', sites: 41, percent: { EMA: 1.7423, PESP: 0.0336, BGMA: 0.4624, UPMA: 0.1126, HALI: 0.0416, PADI: 0.0081, BRMA: 0.1015, RDMA: 0.0085, ASPP: 0.0243, MICR: 0, GRMA: 0.0085, DICO: 0, LOBO: 0, CAUL: 0, AVSP: 0, NEOM: 0, CCAH: 1.6413, CCAR: 0.0246 } },
  { island: 'maui', bin: 'mid', sites: 39, percent: { EMA: 2.1014, PESP: 0.3925, BGMA: 1.6757, UPMA: 0.0864, HALI: 0.1981, PADI: 0, BRMA: 0.0947, RDMA: 0.0345, ASPP: 0.0271, MICR: 0, GRMA: 0.0086, DICO: 0, LOBO: 0, CAUL: 0.0078, AVSP: 0, NEOM: 0, CCAH: 3.0448, CCAR: 0.1033 } },
  { island: 'maui', bin: 'deep', sites: 30, percent: { EMA: 2.4593, PESP: 0.3263, BGMA: 1.7, UPMA: 0.0336, HALI: 0.2119, PADI: 0, BRMA: 0.0132, RDMA: 0.0113, ASPP: 0, MICR: 0.0222, GRMA: 0.0113, DICO: 0, LOBO: 0, CAUL: 0, AVSP: 0, NEOM: 0, CCAH: 2.2122, CCAR: 0.0113 } },
  { island: 'molokai', bin: 'shallow', sites: 50, percent: { EMA: 3.9891, PESP: 4.0036, BGMA: 0.3916, UPMA: 0.2551, HALI: 0.0808, PADI: 0.0136, BRMA: 0.0201, RDMA: 0.1006, ASPP: 0.0541, MICR: 0.0268, GRMA: 0.0333, DICO: 0.0067, LOBO: 0, CAUL: 0, AVSP: 0, NEOM: 0, CCAH: 5.0736, CCAR: 0.0203 } },
  { island: 'molokai', bin: 'mid', sites: 73, percent: { EMA: 3.401, PESP: 3.6061, BGMA: 1.6424, UPMA: 0.4366, HALI: 0.2637, PADI: 0.1387, BRMA: 0.6719, RDMA: 0.5317, ASPP: 0.2026, MICR: 0.2429, GRMA: 0.0091, DICO: 0.014, LOBO: 0.014, CAUL: 0, AVSP: 0, NEOM: 0, CCAH: 2.3359, CCAR: 0.0329 } },
  { island: 'molokai', bin: 'deep', sites: 49, percent: { EMA: 3.295, PESP: 2.4513, BGMA: 1.8757, UPMA: 0.2483, HALI: 0.1447, PADI: 0.1497, BRMA: 0.0068, RDMA: 0.075, ASPP: 0.0478, MICR: 0.381, GRMA: 0.0138, DICO: 0, LOBO: 0, CAUL: 0.0068, AVSP: 0.0068, NEOM: 0.0068, CCAH: 2.0268, CCAR: 0.0549 } },
  { island: 'lanai', bin: 'shallow', sites: 34, percent: { EMA: 1.6537, PESP: 0.3058, BGMA: 0.0399, UPMA: 0.2264, HALI: 0, PADI: 0, BRMA: 0.0098, RDMA: 0.0196, ASPP: 0, MICR: 0.0098, GRMA: 0, DICO: 0, LOBO: 0, CAUL: 0, AVSP: 0, NEOM: 0, CCAH: 1.484, CCAR: 0 } },
  { island: 'lanai', bin: 'mid', sites: 59, percent: { EMA: 2.4409, PESP: 1.1264, BGMA: 0.4408, UPMA: 0.2688, HALI: 0.4097, PADI: 0, BRMA: 0, RDMA: 0.0293, ASPP: 0.1402, MICR: 0, GRMA: 0.0173, DICO: 0.0457, LOBO: 0.0056, CAUL: 0.0056, AVSP: 0.0059, NEOM: 0, CCAH: 1.8192, CCAR: 0.08 } },
  { island: 'lanai', bin: 'deep', sites: 26, percent: { EMA: 2.8065, PESP: 1.4531, BGMA: 1.1529, UPMA: 0.1048, HALI: 0.628, PADI: 0, BRMA: 0.0132, RDMA: 0.0266, ASPP: 0.238, MICR: 0, GRMA: 0.1445, DICO: 0, LOBO: 0.013, CAUL: 0.0267, AVSP: 0.0129, NEOM: 0.0129, CCAH: 1.4281, CCAR: 0.1044 } },
  { island: 'kahoolawe', bin: 'shallow', sites: 26, percent: { EMA: 5.0058, PESP: 0.8879, BGMA: 0.4673, UPMA: 0.3007, HALI: 0, PADI: 0, BRMA: 0.0267, RDMA: 0, ASPP: 0, MICR: 0, GRMA: 0, DICO: 0, LOBO: 0.0402, CAUL: 0, AVSP: 0, NEOM: 0, CCAH: 3.7361, CCAR: 0.0774 } },
  { island: 'kahoolawe', bin: 'mid', sites: 38, percent: { EMA: 3.2878, PESP: 0.8462, BGMA: 1.3143, UPMA: 0.2772, HALI: 0.1724, PADI: 0, BRMA: 0.0349, RDMA: 0.0534, ASPP: 0.0471, MICR: 0, GRMA: 0, DICO: 0.0089, LOBO: 0, CAUL: 0, AVSP: 0, NEOM: 0, CCAH: 0.8926, CCAR: 0.132 } },
  { island: 'kahoolawe', bin: 'deep', sites: 28, percent: { EMA: 2.3875, PESP: 0.3432, BGMA: 1.2585, UPMA: 0.1318, HALI: 0.5951, PADI: 0.012, BRMA: 0.012, RDMA: 0.0126, ASPP: 0, MICR: 0, GRMA: 0.0126, DICO: 0, LOBO: 0, CAUL: 0, AVSP: 0, NEOM: 0, CCAH: 1.0993, CCAR: 0.0838 } },
];
